import type { StorefrontPrice, StorefrontReadiness } from '@/lib/api/schemas/inventory.schema'
import { formatRubleAmount, formatRublePerGram } from '@/lib/market-money'
import { ValueOrReason } from '@/components/warehouse/primitives'

function discountPercent(price: number, oldPrice: number): number | null {
  if (oldPrice <= price) return null
  const percent = Math.round(((oldPrice - price) / oldPrice) * 100)
  return percent >= 1 ? percent : null
}

export function StorefrontPriceCell({ price, pricePerGram, pricePerGramUnavailableReason, drift = null, description }: {
  price: StorefrontPrice
  readiness?: StorefrontReadiness
  pricePerGram?: number | null
  pricePerGramUnavailableReason?: string | null
  drift?: { mark: string; label: string } | null
  description?: string
}) {
  if (price.price === null) {
    return (
      <span data-storefront-state={price.connectionState}>
        <ValueOrReason value={null} reason={price.unavailableReason ?? 'price.not_provided'} />
      </span>
    )
  }
  if (price.connectionState === 'not_connected') {
    return (
      <span data-storefront-state="not_connected">
        <ValueOrReason value={null} reason={price.unavailableReason ?? 'price.not_provided'} />
      </span>
    )
  }
  if (!price.listed) {
    return (
      <span data-storefront-state="absent">
        <ValueOrReason value={null} reason={price.unavailableReason ?? 'price.not_provided'} />
      </span>
    )
  }
  // A Yandex subsidy estimate would be a second, unlabelled Yandex price beside «Продаётся за».
  const hasEstimatedBuyerPrice = price.target !== 'ymarket' && price.estimatedBuyerPrice !== null && price.estimatedBuyerPrice !== undefined
  const displayedPrice = hasEstimatedBuyerPrice ? price.estimatedBuyerPrice : price.price
  const displayedPercent = hasEstimatedBuyerPrice && price.subsidyPercent !== null && price.subsidyPercent !== undefined
    ? Math.round(price.subsidyPercent)
    : price.oldPrice !== null && price.oldPrice !== undefined
      ? discountPercent(price.price, price.oldPrice)
      : null
  return (
    <span className="block min-w-[130px]" data-storefront-state="live" title={description}>
      {price.oldPrice !== null && price.oldPrice !== undefined ? (
        <span
          className="block text-[11px] tabular-nums text-ink-muted line-through"
          data-price-role="discount-base"
        >
          {formatRubleAmount(price.oldPrice)}
        </span>
      ) : null}
      <span
        className="block font-semibold tabular-nums text-ink"
        data-price-role="selling-price"
      >
        {formatRubleAmount(displayedPrice)}
        {displayedPercent !== null ? (
          <span
            className="ml-1.5 align-middle text-[11px] font-medium tabular-nums text-gold-deep"
            data-price-role="discount-percent"
          >
            −{displayedPercent}%
          </span>
        ) : null}
        {drift !== null ? (
          <span
            role="img"
            aria-label={drift.label}
            title={drift.label}
            className="ml-1.5 align-middle text-[12px] font-semibold text-st-unpaid"
            data-price-role="drift"
          >
            {drift.mark}
          </span>
        ) : null}
      </span>
      {pricePerGram !== undefined ? (
        <span className="mt-0.5 block text-[11px] tabular-nums text-ink-muted" data-price-role="per-gram">
          {pricePerGram === null
            ? <ValueOrReason value={null} reason={pricePerGramUnavailableReason ?? 'unit.weight_not_recorded'} />
            : formatRublePerGram(pricePerGram)}
        </span>
      ) : null}
    </span>
  )
}
